import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { IconType } from "react-icons";
import { FiChevronDown, FiChevronUp, FiZap, FiSettings, FiHelpCircle } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import FilialSelector from "../filial-selector";
import { useMediaQuery } from '@mui/system';
import { useHistory, useLocation } from 'react-router-dom';
import { estaEmPeriodoDeTeste, expiraEm, obterDataCadastro } from '../../services/seguranca';
import { rastrearTeste } from '../../services/rastreamentoTeste';
import { FaHourglassHalf } from 'react-icons/fa';

interface CustomButton {
  title: string;
  onClick?: () => void;
  link?: string;
  icon?: IconType;
  dataTour?: string;
}

interface SidebarProps {
  logo: string;
  companyName: string;
  userName: string;
  items: SidebarItem[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  children?: React.ReactNode;
  customButtons?: CustomButton[];
  forceQuickAccessOpen?: boolean;
}

interface SidebarItem {
  title: string;
  link?: string;
  icon: IconType;
  subItems?: SidebarSubItem[];
  corDestaque?: string;
  expansivel?: boolean;
  badge?: string;
}

interface SidebarSubItem {
  title: string;
  link?: string;
  icon?: IconType;
  subItems?: SidebarSubItem[];
  expansivel?: boolean;
}

const QuickAccessWrapper = styled.div<{ collapsed: boolean }>`
  margin-top: 15px;
  margin-bottom: 15px;
  position: relative;
  user-select: none;
`;

const QuickAccessToggle = styled.button<{ collapsed: boolean }>`
  width: 100%;
  padding: ${({ collapsed }) => (collapsed ? "8px 5px" : "12px 16px")};
  border: none;
  border-radius: 8px;
  background: linear-gradient(90deg, #143C8F 0%, #335EDB 100%);
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "space-between")};
  gap: 8px;
  position: relative;
  overflow: hidden;
  min-height: 44px;
  user-select: none;

  &:hover {
    filter: brightness(1.05);
  }

  &:active {
    filter: brightness(0.95);
  }

  .toggle-text {
    opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
    width: ${({ collapsed }) => (collapsed ? "0" : "auto")};
    display: ${({ collapsed }) => (collapsed ? "none" : "flex")};
    transition: opacity 0.3s ease, width 0.3s ease;
    white-space: nowrap;
    overflow: hidden;
    align-items: center;
    gap: 8px;
    color: #ffffff;
  }

  .toggle-icon {
    flex-shrink: 0;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${({ collapsed }) => (collapsed ? "100%" : "auto")};
    color: #ffffff;
  }
`;

const QuickAccessDropdown = styled.div<{ isOpen: boolean; collapsed: boolean }>`
  position: ${({ collapsed }) => (collapsed ? 'fixed' : 'absolute')};
  top: ${({ collapsed }) => (collapsed ? 'auto' : '100%')};
  left: ${({ collapsed }) => (collapsed ? '80px' : '0')};
  margin-top: 5px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  z-index: 1001;
  max-height: ${({ isOpen }) => (isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  min-width: ${({ collapsed }) => (collapsed ? '200px' : '220px')};
  user-select: none;
`;

const QuickAccessButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #1a1a1a;
  font-size: 0.95rem;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  white-space: nowrap;
  user-select: none;

  &:hover {
    background-color: rgba(58, 92, 141, 0.1);
    color: #1a1a1a;
  }

  .button-icon {
    flex-shrink: 0;
    font-size: 1.1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1a1a1a;
  }

  &:hover .button-icon {
    color: #1a1a1a  ;
  }
`;

const SidebarContainer = styled.div<{ collapsed: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "80px" : "250px")};
  background-color: #ffffff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding: 20px 10px 20px 20px;
  transition: width 0.3s;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  user-select: none;

  /* Estilização da barra de rolagem */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }

  @media (max-width: 768px) {
    width: ${({ collapsed }) => (collapsed ? "0" : "250px")};
    padding: ${({ collapsed }) => (collapsed ? "0" : "20px 10px 20px 20px")};
    position: fixed;
    z-index: 1000;
    box-shadow: ${({ collapsed }) => (collapsed ? "none" : "2px 0 10px rgba(0,0,0,0.1)")};
  }
`;

const Overlay = styled.div<{ show: boolean }>`
  display: ${({ show }) => (show ? 'block' : 'none')};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  cursor: pointer;
  user-select: none;

  @media (min-width: 769px) {
    display: none;
  }
`;

const LogoContainer = styled.div<{ collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
  min-height: 60px;
  cursor: pointer;
  user-select: none;

  @media (max-width: 768px) {
    display: flex;
    opacity: ${({ collapsed }) => (collapsed ? "0" : "1")};
    transition: opacity 0.3s;
  }
`;

const Logo = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 10%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CompanyName = styled.h1<{ collapsed: boolean }>`
  font-size: 1rem;
  color: #1F467E;
  font-weight: 600;
  margin: 0;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  overflow: hidden;
  transition: opacity 0.3s, transform 0.3s;
  transform: ${({ collapsed }) =>
    collapsed ? "translateX(-20px)" : "translateX(0)"};
`;

const LocaSmartProTitle = styled.h1<{ collapsed: boolean }>`
  font-size: 1.3rem;
  color: #1F467E;
  font-weight: 600;
  margin: 0;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  white-space: nowrap;
  overflow: hidden;
  transition: opacity 0.3s, transform 0.3s;
  transform: ${({ collapsed }) =>
    collapsed ? "translateX(-20px)" : "translateX(0)"};
`;

const TituloComSelo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  min-width: 0;
`;

// Dia de calendário de uma data "08/10/2026 23:26:16" (a hora não conta).
const diaDaData = (dataBR: string): Date | null => {
  const m = (dataBR || '').match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  return m ? new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : null;
};
const diasEntre = (de: Date, ate: Date) => Math.round((ate.getTime() - de.getTime()) / 86400000);

// Quanto falta do período de teste: no dia do vencimento é 0. O total vem do cadastro até o vencimento.
const contagemDoTeste = (cadastro: string, expiracao: string) => {
  const fim = diaDaData(expiracao);
  if (!fim) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const restantes = Math.max(0, diasEntre(hoje, fim));
  const inicio = diaDaData(cadastro);
  const total = Math.max(restantes, 1, inicio ? diasEntre(inicio, fim) : 7);
  return { restantes, fracao: restantes / total };
};

// Selo discreto do período de teste, no lugar do antigo banner "Período de teste".
const SeloPlano = styled.button<{ collapsed: boolean }>`
  display: ${({ collapsed }) => (collapsed ? "none" : "inline-flex")};
  align-items: center;
  padding: 1px 8px;
  border: 1px solid #f5d27a;
  border-radius: 999px;
  background: #fff8e1;
  color: #8a5a00;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover { background: #ffefc2; }
`;

// Contagem regressiva discreta do teste, logo abaixo do Acesso Rápido. Fica vermelha nos dois últimos dias.
const CartaoTeste = styled.button<{ collapsed: boolean; $urgente: boolean }>`
  display: ${({ collapsed }) => (collapsed ? "none" : "flex")};
  flex-direction: column;
  gap: 5px;
  width: 100%;
  margin: -6px 0 12px;
  padding: 2px 4px;
  border: none;
  background: none;
  color: ${({ $urgente }) => ($urgente ? "#b42318" : "#6b7280")};
  font: inherit;
  text-align: left;
  cursor: pointer;

  .linha { display: flex; align-items: center; gap: 6px; font-size: 0.74rem; }
  .linha > span:not(.acao) { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .linha svg { flex: none; font-size: 0.7rem; color: ${({ $urgente }) => ($urgente ? "#d92d20" : "#d39e00")}; }
  .acao { margin-left: auto; font-weight: 600; color: ${({ $urgente }) => ($urgente ? "#b42318" : "#1F467E")}; white-space: nowrap; }
  &:hover .acao { text-decoration: underline; }
  .barra { display: block; height: 3px; border-radius: 999px; background: #eef0f3; overflow: hidden; }
  .barra i { display: block; height: 100%; border-radius: 999px; background: ${({ $urgente }) => ($urgente ? "#d92d20" : "#e0a526")}; }
`;

const Divider = styled.div`
  height: 1px;
  background-color: #e5e7eb;
  margin: 10px 0;
`;

const Menu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  flex-grow: 1;
  overflow-y: auto;
  user-select: none;

  /* Estilização da barra de rolagem */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const MenuItem = styled.li`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  cursor: pointer;
  position: relative;
`;

const MenuLink = styled.a<{ collapsed: boolean; corDestaque?: string }>`
  display: flex;
  text-decoration: none;
  align-items: center;
  padding: 10px;
  border-radius: 20px;
  color: #333;
  font-size: 1rem;
  background-color: transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  overflow: hidden;
  position: relative;
  user-select: none;

  &:hover {
    background-color: rgba(58, 92, 141, 0.1);
    color: #3A5C8D;
  }

  .menu-text {
    display: inline-block;
    padding: 1px 10px;
    background-color: ${({ corDestaque }) => corDestaque || "transparent"};
    border-radius: 5px;
    color: ${({ corDestaque }) => (corDestaque ? "#ffffff" : "#333")};
    transition: all 0.2s ease;
  }

  span {
    opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
    transition: opacity 0.3s, transform 0.3s;
    transform: ${({ collapsed }) =>
    collapsed ? "translateX(-20px)" : "translateX(0)"};
  }
`;

const NovoBadge = styled.span`
  margin-left: 8px;
  padding: 1px 7px;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #ffffff;
  background-color: #22a06b;
  border-radius: 10px;
  white-space: nowrap;
`;

const IconWrapper = styled.div<{ collapsed: boolean }>`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  margin-right: 10px;
  padding: 5px;
  justify-content: center;
  font-size: 1.1rem;
  background-color: #f2f2f2;
  border-radius: 5px;
  transition: all 0.2s ease;

  ${MenuLink}:hover & {
    background-color: rgba(58, 92, 141, 0.2);
  }
`;

const SubMenu = styled.ul<{ isOpen: boolean }>`
  list-style: none;
  padding: 0;
  margin: 5px 0 0 20px;
  max-height: ${({ isOpen }) => (isOpen ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const SubMenuItem = styled.li`
  margin-bottom: 2px;
`;

const SubMenuLink = styled.a`
  text-decoration: none;
  color: #555;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 5px;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background-color: rgba(58, 92, 141, 0.1);
    color: #3A5C8D;
  }
`;

const Footer = styled.div<{ collapsed: boolean }>`
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  gap: 10px;
  min-height: 120px;
  user-select: none;

  .footer-info {
    display: flex;
    flex-direction: column;
    align-items: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
    opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
    transition: opacity 0.3s;
    width: 100%;
  }

  .footer-item {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    color: #333;
    font-size: 1rem;
    width: 100%;
    padding: 8px;
    border-radius: 5px;
    justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
    transition: all 0.2s ease;

    span {
      opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
      white-space: nowrap;
      transition: opacity 0.3s, transform 0.3s;
      transform: ${({ collapsed }) =>
    collapsed ? "translateX(-20px)" : "translateX(0)"};
    }

    &:hover {
      background-color: rgba(58, 92, 141, 0.1);
      color: #3A5C8D;
    }
  }

  @media (max-width: 768px) {
    opacity: ${({ collapsed }) => (collapsed ? "0" : "1")};
    transition: opacity 0.3s;
  }
`;

const RotatingIcon = styled.div<{ isOpen: boolean }>`
  transition: transform 0.3s ease;
  transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  margin-left: auto;
  color: #333;
`;

const RotatingIconWhite = styled.div<{ isOpen: boolean }>`
  transition: transform 0.3s ease;
  transform: ${({ isOpen }) => (isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  margin-left: auto;
  color: #fff;
`;

const SubMenuComponent: React.FC<{
  subItems: SidebarSubItem[];
  collapsed: boolean;
  onItemClick?: () => void;
}> = ({ subItems, collapsed, onItemClick }) => {
  const [openSubItems, setOpenSubItems] = useState<string | null>(null);
  const history = useHistory();

  const toggleSubItem = (title: string) => {
    setOpenSubItems((prev) => (prev === title ? null : title));
  };

  return (
    <SubMenu isOpen={true}>
      {subItems.map((subItem) => (
        <SubMenuItem key={subItem.title}>
          <SubMenuLink
            href={subItem.link}
            onClick={(e) => {
              e.preventDefault();
              if (!subItem.link && subItem.expansivel) {
                toggleSubItem(subItem.title);
              } else if (subItem.link) {
                history.push(subItem.link);
                if (onItemClick) onItemClick();
              }
            }}
          >
            {subItem.icon && <subItem.icon style={{ fontSize: "0.9rem" }} />}
            {subItem.title}
            {subItem.expansivel && (
              <RotatingIcon isOpen={openSubItems === subItem.title}>
                <FiChevronDown />
              </RotatingIcon>
            )}
          </SubMenuLink>
          {subItem.subItems && openSubItems === subItem.title && !collapsed && (
            <SubMenuComponent
              subItems={subItem.subItems}
              collapsed={collapsed}
              onItemClick={onItemClick}
            />
          )}
        </SubMenuItem>
      ))}
    </SubMenu>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  logo,
  companyName,
  userName,
  items,
  collapsed = false,
  onToggleCollapse,
  customButtons,
  forceQuickAccessOpen,
}) => {
  const dispatch = useAppDispatch();
  const [openItems, setOpenItems] = useState<string | null>(null);
  const [quickAccessOpen, setQuickAccessOpen] = useState(false);
  const [temporarilyExpanded, setTemporarilyExpanded] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [showOverlay, setShowOverlay] = useState(false);
  const location = useLocation();
  const quickAccessRef = useRef<HTMLDivElement>(null);

  const history = useHistory();

  // Fecha o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickAccessRef.current && !quickAccessRef.current.contains(event.target as Node)) {
        setQuickAccessOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fecha o menu quando a rota muda (mobile apenas)
  useEffect(() => {
    if (isMobile && !collapsed && onToggleCollapse) {
      onToggleCollapse();
    }
  }, [location.pathname, isMobile]);

  // Force open quick access from parent (e.g. tour)
  useEffect(() => {
    if (forceQuickAccessOpen) {
      setQuickAccessOpen(true);
    }
  }, [forceQuickAccessOpen]);

  // Reset temporary expansion when collapsed state changes
  useEffect(() => {
    setTemporarilyExpanded(false);
    setQuickAccessOpen(false);
  }, [collapsed]);

  useEffect(() => {
    if (isMobile) {
      setShowOverlay(!collapsed);
    } else {
      setShowOverlay(false);
    }
  }, [collapsed, isMobile]);

  const toggleItem = (title: string) => {
    setOpenItems((prev) => (prev === title ? null : title));
  };

  const toggleQuickAccess = () => {
    setQuickAccessOpen((prev) => !prev);
  };

  const handleItemClick = (e: React.MouseEvent, item: SidebarItem) => {
    e.preventDefault();
    if (collapsed && onToggleCollapse) {
      onToggleCollapse();
      if (item.expansivel) {
        toggleItem(item.title);
      }
    } else if (!item.link && item.expansivel) {
      toggleItem(item.title);
    } else if (item.link && e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      // Navegação client-side (SPA) em vez do GET de página inteira do <a href>,
      // que num servidor sem fallback de SPA resulta em "Cannot GET /rota".
      // Mantém Ctrl/Cmd/Shift/clique-do-meio para abrir em nova aba.
      e.preventDefault();
      history.push(item.link);
      if (isMobile && onToggleCollapse) {
        onToggleCollapse();
      }
    }
  };

  const handleLogoClick = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const handleLogout = () => {
    dispatch({ type: "LOG_OUT" });
    setTimeout(() => {
      window.location.href = "/";
    }, 250);
  };

  const handleOverlayClick = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const handleQuickAccessButtonClick = (btn: CustomButton) => {
    if (btn.onClick) {
      btn.onClick();
    } else if (btn.link) {
      history.push(btn.link);
    }
    setQuickAccessOpen(false);
  };

  const isActuallyCollapsed = collapsed && !temporarilyExpanded;
  const tokenSidebar = useAppSelector(state => state.token);
  const emPeriodoDeTeste = !!tokenSidebar && estaEmPeriodoDeTeste(tokenSidebar);
  const contagem = emPeriodoDeTeste ? contagemDoTeste(obterDataCadastro(tokenSidebar), expiraEm(tokenSidebar)) : null;

  return (
    <>
      <Overlay show={showOverlay} onClick={handleOverlayClick} />
      <SidebarContainer collapsed={isActuallyCollapsed}>
        <LogoContainer
          collapsed={isActuallyCollapsed}
          onClick={handleLogoClick}
        >
          <Logo src={logo} alt="Logo" />
          <TituloComSelo>
            <LocaSmartProTitle collapsed={isActuallyCollapsed}>
              LocaSmart Pro
            </LocaSmartProTitle>
            {emPeriodoDeTeste && (
              <SeloPlano
                type="button"
                collapsed={isActuallyCollapsed}
                title={`Acesso completo até ${expiraEm(tokenSidebar)}. Clique para ver os planos.`}
                onClick={e => { e.stopPropagation(); rastrearTeste('selo_teste_clicado'); history.push('/planos'); }}
              >
                Plano de Teste
              </SeloPlano>
            )}
          </TituloComSelo>
        </LogoContainer>
        
        {/* Botão de Acesso Rápido dentro do sidebar */}
        {customButtons && customButtons.length > 0 && (
          <QuickAccessWrapper ref={quickAccessRef} collapsed={isActuallyCollapsed}>
            <QuickAccessToggle 
              onClick={toggleQuickAccess} 
              collapsed={isActuallyCollapsed}
            >
              <div className="toggle-text">
                <FiZap />
                Acesso Rápido
              </div>
              {!isActuallyCollapsed && (
                <RotatingIconWhite isOpen={quickAccessOpen}>
                  {quickAccessOpen ? <FiChevronUp /> : <FiChevronDown />}
                </RotatingIconWhite>
              )}
              {isActuallyCollapsed && (
                <div className="toggle-icon">
                  <FiZap />
                </div>
              )}
            </QuickAccessToggle>
            
            <QuickAccessDropdown isOpen={quickAccessOpen} collapsed={isActuallyCollapsed}>
              {customButtons.map((btn, index) => (
                <QuickAccessButton
                  key={index}
                  onClick={() => handleQuickAccessButtonClick(btn)}
                  data-tour={btn.dataTour}
                >
                  {btn.icon && (
                    <div className="button-icon">
                      <btn.icon />
                    </div>
                  )}
                  {btn.title}
                </QuickAccessButton>
              ))}
            </QuickAccessDropdown>
          </QuickAccessWrapper>
        )}

        {emPeriodoDeTeste && contagem && (
          <CartaoTeste
            type="button"
            collapsed={isActuallyCollapsed}
            $urgente={contagem.restantes <= 2}
            onClick={() => { rastrearTeste('contagem_teste_clicada'); history.push('/planos'); }}
            title={`Acesso completo até ${expiraEm(tokenSidebar)}`}
          >
            <span className="linha">
              <FaHourglassHalf />
              <span>{contagem.restantes === 0 ? 'Último dia de teste' : contagem.restantes === 1 ? '1 dia de teste' : `${contagem.restantes} dias de teste`}</span>
              <span className="acao">Ver planos</span>
            </span>
            <span className="barra"><i style={{ width: `${Math.max(6, contagem.fracao * 100)}%` }} /></span>
          </CartaoTeste>
        )}

        <FilialSelector />

        <Menu>
          {items.map((item) => (
            <MenuItem key={item.title}>
              <MenuLink
                href={item.link}
                collapsed={isActuallyCollapsed}
                corDestaque={item.corDestaque}
                onClick={(e) => handleItemClick(e, item)}
              >
                <IconWrapper collapsed={isActuallyCollapsed}>
                  <item.icon />
                </IconWrapper>
                <span className="menu-text">
                  {!isActuallyCollapsed && item.title}
                </span>
                {item.badge && !isActuallyCollapsed && (
                  <NovoBadge>{item.badge}</NovoBadge>
                )}
                {item.expansivel && !isActuallyCollapsed && (
                  <RotatingIcon isOpen={openItems === item.title}>
                    <FiChevronDown />
                  </RotatingIcon>
                )}
              </MenuLink>
              {item.subItems && openItems === item.title && !isActuallyCollapsed && (
                <SubMenuComponent
                  subItems={item.subItems}
                  collapsed={isActuallyCollapsed}
                  onItemClick={() => {
                    if (isMobile && onToggleCollapse) {
                      onToggleCollapse();
                    }
                  }}
                />
              )}
            </MenuItem>
          ))}
        </Menu>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;